import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  IoPerson,
  IoMailOutline,
  IoLockClosedOutline,
  IoKeypadOutline,
  IoEye,
  IoEyeOff,
  IoArrowBack,
} from "react-icons/io5";
import { http } from "../lib/http";
import "./AuthForms.css";
import { useI18n } from "../contexts/I18n";

const PUB = process.env.PUBLIC_URL || "";
const WELCOME_VIDEO_SRC = `${PUB}/videos/welcome.mp4`;
const HERO_BRAND_LOGO = `${PUB}/images/ehgezly-hero-logo.png?v=4`;

export default function ForgotPassword() {
  const { t, lang } = useI18n();
  const isRtl = lang === "ar";
  const [step, setStep] = useState("email"); // 'email' | 'code'
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const videoRef = useRef(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return undefined;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      el.pause();
      return undefined;
    }
    el.muted = true;
    const play = () => {
      const p = el.play();
      if (p?.catch) p.catch(() => {});
    };
    play();
    el.addEventListener("loadeddata", play);
    return () => el.removeEventListener("loadeddata", play);
  }, []);

  const clearAlerts = () => {
    if (error || message) {
      setError("");
      setMessage("");
    }
  };

  const handleSendCode = async (e) => {
    e?.preventDefault();
    setError("");
    setMessage("");
    if (!email.trim()) return setError(t("emailRequired"));
    setLoading(true);
    try {
      const res = await http.fetch("/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Error sending reset code");
      setMessage(t("resetSent"));
      setStep("code");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    if (!code.trim() || code.trim().length !== 4) {
      return setError(t("verificationCodeRequired") || "Please enter the 4-digit code");
    }
    if (!password) return setError(t("passwordRequired") || "Password is required");
    if (password !== confirm) return setError(t("passwordMismatch") || "Passwords do not match");
    setLoading(true);
    try {
      const res = await http.fetch("/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          verificationCode: code.trim(),
          newPassword: password,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Error resetting password");
      setMessage(t("passwordUpdated") || "Password updated! You can sign in now.");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`auth-page auth-page--split auth-page--forgot${isRtl ? " rtl" : ""}`}
      dir={isRtl ? "rtl" : "ltr"}
    >
      <aside className="auth-brand auth-brand--showcase" aria-label={t("resetPassword")}>
        <div className="auth-brand__video" aria-hidden="true">
          <video
            ref={videoRef}
            className="auth-brand__video-el"
            src={WELCOME_VIDEO_SRC}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          />
          <div className="auth-brand__video-veil" />
        </div>
        <div className="auth-brand__hero-mark">
          <div className="auth-brand__hero-logo-wrap">
            <img
              src={HERO_BRAND_LOGO}
              alt=""
              className="auth-brand__hero-logo"
              draggable="false"
            />
          </div>
        </div>
      </aside>

      <main className="auth-main">
        <div className="auth-form-panel">
          <header className="auth-form-head">
            <div className="auth-form-head__mark" aria-hidden="true">
              <span className="auth-avatar">
                <IoPerson className="auth-avatar__icon" />
              </span>
            </div>
            <h2>{t("resetPassword")}</h2>
            <p>
              {step === "email"
                ? t("resetSubtitle")
                : t("resetSubtitleCode") ||
                  "Enter the 4-digit code sent to your email and choose a new password."}
            </p>
          </header>

          {error && (
            <div className="error-message" role="alert">
              {error}
            </div>
          )}
          {message && (
            <div className="success-message" role="status">
              {message}
            </div>
          )}

          {step === "email" && (
            <form className="auth-form" onSubmit={handleSendCode} aria-busy={loading} noValidate>
              <div className="form-group">
                <label className="form-label form-label-with-icon" htmlFor="forgot-email">
                  <span className="form-label-icon" aria-hidden="true">
                    <IoMailOutline className="form-label-icon-svg" />
                  </span>
                  <span className="form-label-text">{t("emailAddress")}</span>
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    clearAlerts();
                  }}
                  placeholder={t("emailPlaceholder")}
                  className="form-input"
                  required
                  autoComplete="email"
                />
              </div>

              <button type="submit" className="auth-button" disabled={loading}>
                <span className="button-content">
                  {loading ? (
                    <span className="loading-spinner" />
                  ) : (
                    t("sendResetCode") || t("sendResetLink")
                  )}
                </span>
              </button>
            </form>
          )}

          {step === "code" && (
            <form
              className="auth-form"
              onSubmit={handleResetPassword}
              aria-busy={loading}
              noValidate
            >
              <div className="form-group">
                <label className="form-label form-label-with-icon" htmlFor="forgot-code">
                  <span className="form-label-icon" aria-hidden="true">
                    <IoKeypadOutline className="form-label-icon-svg" />
                  </span>
                  <span className="form-label-text">
                    {t("verificationCode") || "4-digit code"}
                  </span>
                </label>
                <input
                  id="forgot-code"
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value.replace(/\D/g, ""));
                    clearAlerts();
                  }}
                  placeholder="1234"
                  className="form-input verification-code-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label form-label-with-icon" htmlFor="forgot-password">
                  <span className="form-label-icon" aria-hidden="true">
                    <IoLockClosedOutline className="form-label-icon-svg" />
                  </span>
                  <span className="form-label-text">{t("newPassword") || "New Password"}</span>
                </label>
                <div className="auth-password-field">
                  <input
                    id="forgot-password"
                    type={showPwd ? "text" : "password"}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      clearAlerts();
                    }}
                    placeholder="••••••••"
                    className="form-input"
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPwd((s) => !s)}
                    aria-pressed={showPwd}
                    aria-label={showPwd ? t("hidePassword") : t("showPassword")}
                  >
                    {showPwd ? <IoEye /> : <IoEyeOff />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label form-label-with-icon" htmlFor="forgot-confirm">
                  <span className="form-label-icon" aria-hidden="true">
                    <IoLockClosedOutline className="form-label-icon-svg" />
                  </span>
                  <span className="form-label-text">
                    {t("confirmPassword") || "Confirm Password"}
                  </span>
                </label>
                <div className="auth-password-field">
                  <input
                    id="forgot-confirm"
                    type={showConfirm ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => {
                      setConfirm(e.target.value);
                      clearAlerts();
                    }}
                    placeholder="••••••••"
                    className="form-input"
                    required
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirm((s) => !s)}
                    aria-pressed={showConfirm}
                    aria-label={showConfirm ? t("hidePassword") : t("showPassword")}
                  >
                    {showConfirm ? <IoEye /> : <IoEyeOff />}
                  </button>
                </div>
              </div>

              <button type="submit" className="auth-button" disabled={loading}>
                <span className="button-content">
                  {loading ? (
                    <span className="loading-spinner" />
                  ) : (
                    t("setNewPassword") || "Set New Password"
                  )}
                </span>
              </button>
            </form>
          )}

          <div className="auth-footer">
            <button
              type="button"
              className="back-button"
              onClick={() => (step === "email" ? navigate("/login") : setStep("email"))}
            >
              <IoArrowBack className="back-icon" aria-hidden="true" />
              {t("backToSignIn")}
            </button>
            <p>
              {t("dontHaveAccount")}{" "}
              <NavLink to="/register" className="auth-link">
                {t("signUpHere")}
              </NavLink>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
